import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import WhereToVoteIcon from '@mui/icons-material/WhereToVote';

import { Link, Tab, Tabs } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { updateUserAccount } from '../api/auth';
import { AccountContext } from '../context/account';
import { trackEvent } from '../helpers/tracking';
import { ErrorMessage, SuccessMessage } from './error-message';
import { ModalTitle } from './modal-title';
import { OrganisationDiagramModal } from './organisation-diagram-modal';
import { OrganisationSettingsModal } from './organisation-settings-modal';
import { SettingsValidationsTable } from './settings-validations-table';
import { SuperAdminSettingsDataSources } from './super-admin-settings-data-sources';
import { SuperAdminSettingsOrders } from './super-admin-settings-orders';
import { SuperAdminSettingsOrganisations } from './super-admin-settings-organisations';
import { SuperAdminSettingsUsers } from './super-admin-settings-users';

export const showApiButton = import.meta.env.VITE_ENABLE_API_ACCESS === 'true'

interface SuperAdminSettingsModalProps {
  open: boolean;
  handleClose: () => void;
}

export const SuperAdminSettingsModal = ({ open, handleClose }: SuperAdminSettingsModalProps) => {
  const [showAllUsers, setShowAllUsers] = useState(false)
  const [tabIndex, setTabIndex] = React.useState(0);
  

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const [userOrganisationId, setUserOrganisationId] = useState<number | null>(null)
  const [showOrganisationModal, setShowOrganisationModal] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [modalError, setModalError] = useState<string[]>([]);
  const [showMessage, setShowMessage] = useState(false)
  const [isEdited, setIsEdited] = useState(false)
  const [enableNotifications, setEnableNotifications] = useState(false)

  const { 
    account, 
    refetchUserDetails, 
    companyPurchaseRequests,
  } = useContext(AccountContext);

  const onClose = () => {
    setShowMessage(false)
    setModalError([])    
    handleClose()
  }

  useEffect(() => {
    setShowMessage(false)
    setModalError([])
    setIsEdited(false)
  }, [open])

  useEffect(() => {
    if (open && account.uid) {
      trackEvent("user-settings-modal", {uid: account.uid})
    }
  }, [open, account.uid])

  useEffect(() => {
    if (account !== null) {
      setFirstName(account.first_name || '')
      setLastName(account.last_name || '')
      setEnableNotifications(account.notifications || false)
    }
  }, [account, open])

  const validateInputs = () => {
    let isValid = true;
    if (!firstName || !lastName) {
      isValid = false;
      setModalError( e => [...e, `Your first name or last name cannot be empty.`])
    }
    return isValid;
  };

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setModalError([])
    const isValid = validateInputs()
    
    if (isValid) {
      const execute = async () => {
        const {error, message} = await updateUserAccount({
          first_name: firstName,
          last_name: lastName,
          email: account.email || null,
          // uid: account.uid || null,
          notifications: enableNotifications,
        })
        if (error !== 200) {
          setModalError( e => [...e, `${message}`])
        } else {
          setIsEdited(false)
          setShowMessage(true)
          refetchUserDetails()
        }
      }
      execute()      
    }
  };

  const userOrganisationAdmin = useMemo(() => {
    if (account) {
      const {organisation} = account
      if (organisation && Array.isArray(organisation)) {
        return organisation.filter( org => {
          return org.role === 'admin' || org.role === 'super-admin'
        })
      }
    }
    return []
  }, [account])
  
  const userOrganisationNonAdmin = useMemo(() => {
    if (account) {
      const {organisation} = account
      if (organisation && Array.isArray(organisation)) {
        return organisation.filter( org => {
          return org.role === 'member' || org.role === 'external-user'
        })
      }
    }
    return []
  }, [account])

  const hasMultipleOrganisations = useMemo(() => {
    if (account) {
      const {organisation} = account
      return (organisation && Array.isArray(organisation) && organisation.length > 1)
    }
    return false
  }, [account])

  const activeRequests = useMemo(() => {
    return companyPurchaseRequests?.filter(({status}) => status === 'pending' || status === 'processing') 
  }, [companyPurchaseRequests])

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
      >
        <ModalTitle onClose={onClose}>App super admin management</ModalTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
        >
          <>
            <div>
              <p>As a super-admin you can view and manage all organisation data, users, tokens and validation points.</p>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                allowScrollButtonsMobile
              >
                <Tab label={'Users'} icon={<PeopleIcon />} />
                <Tab label={'Organisations'} icon={<BusinessIcon />} />
                <Tab label={'Public validations'} icon={<WhereToVoteIcon />} />
                <Tab label={'Private Validations'} icon={<WhereToVoteIcon />} />
                <Tab label={'Orders'} icon={<ShoppingCartIcon />} />
                <Tab label={'Data sources'} icon={<TravelExploreIcon />} />
              </Tabs>
            </div>

            {showMessage && <SuccessMessage message={'Your account has been updated.'} />}
            <ErrorMessage message={modalError} />
            {
              tabIndex === 0 && 
              <SuperAdminSettingsUsers />   
            }
            {
              tabIndex === 1 &&
              <SuperAdminSettingsOrganisations />
            }
            {
              tabIndex === 2 &&
              <SettingsValidationsTable isPublicValidation={false} />
            }
            {
              tabIndex === 3 &&
              <SettingsValidationsTable isPublicValidation={true} />
            }
            {
              tabIndex === 4 &&
              <SuperAdminSettingsOrders />
            }
            {
              tabIndex === 5 &&
              <SuperAdminSettingsDataSources />
            }
          </>
          {hasMultipleOrganisations && userOrganisationAdmin.length > 2 && 
            <>
              <Link onClick={() => {
                setShowOrganisationModal(true)
              }}>Show your organisation diagram</Link>
            </>
          }
        </DialogContent>
        
        {isEdited ? 
          <>
            <DialogActions sx={{ pb: 3, px: 3 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" type="submit" onClick={onSubmit}>
                Save
              </Button>
            </DialogActions>
          </>
          :
          <>
            <DialogActions sx={{ pb: 3, px: 3 }}>
              <Button onClick={onClose}>Close</Button>
            </DialogActions>
          </>
        }
      </Dialog>
      <OrganisationDiagramModal open={showOrganisationModal} handleClose={() => {
        setShowOrganisationModal(false)
      }} />
      {userOrganisationId !== null &&
        <OrganisationSettingsModal
          id={userOrganisationId}
          handleClose={() => {
            setUserOrganisationId(null)
          }}
          onUpdateOrganisationId={(id: number | string | null) => {
            setUserOrganisationId(id !== null ? Number(id) : null)
          }}
        />
      }
    </>
  );
}
